import { Controller } from '@hotwired/stimulus'
import Splide from '@splidejs/splide'

// Connects to data-controller="carousel"
export default class extends Controller {
  static values = {
    drag: { type: String, default: 'free' },
    type: { type: String, default: 'slide' },
    arrows: { type: Boolean, default: false },
    pagination: { type: Boolean, default: false },
    snap: { type: Boolean, default: true },
    omitEnd: { type: Boolean, default: true },
    mediaQuery: { type: String, default: 'min' },
    focus: { type: Number, default: 0 },
    autoplay: { type: Boolean, default: false },
    interval: { type: Number, default: 3000 },
    pauseOnHover: { type: Boolean, default: true },
    perPage: { type: Number },
    autoWidth: { type: Boolean, default: false },
    mountMinWidth: { type: Number, default: 0 },
    perMove: { type: Number },
    height: { type: String },
    gap: { type: String },
    // Splide accepte '1rem' (symétrique) ou { left: '1rem', right: '3rem' } — objet
    // JSON pour un peek asymétrique (carte courante calée à gauche).
    padding: Object,
    speed: { type: Number, default: 600 },
    breakpoints: { type: Object },
  }

  initialize() {
    this.pristineHtml = this.element.innerHTML
    this.carousel = new Splide(this.element, this.#options)
  }

  connect() {
    if (this.mountMinWidthValue > 0) {
      // Sous ce seuil, pas de Splide : le markup défile en CSS natif.
      this.media = window.matchMedia(`(min-width: ${this.mountMinWidthValue}px)`)
      this.media.addEventListener('change', this.syncMount)
      this.syncMount()
    } else {
      this.mounted = true
      this.carousel.mount()
    }
  }

  disconnect() {
    this.media?.removeEventListener('change', this.syncMount)
    if (this.mounted) this.carousel.destroy()
  }

  syncMount = () => {
    if (this.media.matches && !this.mounted) {
      this.mounted = true
      this.carousel = new Splide(this.element, this.#options)
      this.carousel.mount()
    } else if (!this.media.matches && this.mounted) {
      this.mounted = false
      this.carousel.destroy(true)
      this.element.innerHTML = this.pristineHtml
      // destroy laisse is-initialized, et `.is-initialized:not(.is-active)` chez
      // Splide passe la liste en display:block — les tags s'empilaient.
      this.element.classList.remove('is-initialized')
    }
  }

  get #options() {
    const options = {
      type: this.typeValue,
      drag: this.dragValue,
      arrows: this.arrowsValue,
      snap: this.snapValue,
      mediaQuery: this.mediaQueryValue,
      pagination: this.paginationValue,
      focus: this.focusValue,
      omitEnd: this.omitEndValue,
    }
    if (this.autoplayValue) {
      options.autoplay = true
      options.interval = this.intervalValue
      options.pauseOnHover = this.pauseOnHoverValue
    }
    if (this.hasPerPageValue) options.perPage = this.perPageValue
    if (this.autoWidthValue) options.autoWidth = true
    if (this.hasPerMoveValue) options.perMove = this.perMoveValue
    if (this.hasGapValue) options.gap = this.gapValue
    if (this.hasHeightValue) options.height = this.heightValue
    if (this.hasPaddingValue) options.padding = this.paddingValue
    if (this.hasSpeedValue) options.speed = this.speedValue
    if (this.hasBreakpointsValue) options.breakpoints = this.breakpointsValue

    return options
  }
}
